import React, { useEffect, useRef, useState } from "react";
import { Bell, CheckCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { useLang } from "../i18n";
import { fmtRelative } from "../utils";

export default function NotificationsBell() {
  const { t, pick } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ notifications: [], unread_count: 0 });
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setData(data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 45000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const openItem = async (n) => {
    await api.post(`/notifications/${n.id}/read`).catch(() => {});
    setOpen(false);
    if (n.link) navigate(n.link);
    load();
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notifications-bell" onClick={() => { setOpen(!open); if (!open) load(); }}
        aria-label={t("notifs.title")}
        className="relative w-11 h-11 rounded-2xl bg-[#171347]/80 border border-white/12 flex items-center justify-center hover:border-[#9C42F5]/50 transition-colors">
        <Bell className="w-5 h-5 text-[#D8D4F2]" />
        {data.unread_count > 0 && (
          <span data-testid="notifications-badge" className="absolute -top-1.5 -end-1.5 min-w-[1.25rem] h-5 px-1 rounded-full bg-gradient-to-r from-[#EF4D9A] to-[#FF63B5] text-[11px] font-black flex items-center justify-center glow-pink">
            {data.unread_count}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notifications-panel" className="absolute end-0 top-14 w-80 max-w-[85vw] magic-panel p-3 z-50 animate-pop">
          <div className="flex items-center justify-between px-2 pb-2">
            <h4 className="font-extrabold">{t("notifs.title")}</h4>
            <button data-testid="mark-all-read-btn" onClick={async () => { await api.post("/notifications/read-all"); load(); }}
              className="text-xs text-[#27C9EE] hover:text-white flex items-center gap-1 font-bold transition-colors">
              <CheckCheck className="w-3.5 h-3.5" /> {t("notifs.mark_all_read")}
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto space-y-1.5">
            {data.notifications.length === 0 && (
              <p className="text-center text-sm text-[#A5A0D6] py-8">{t("notifs.empty")}</p>
            )}
            {data.notifications.map((n) => (
              <button key={n.id} data-testid={`notification-${n.id}`} onClick={() => openItem(n)}
                className={`w-full text-start rounded-xl px-3 py-2.5 transition-colors border ${n.read ? "bg-transparent border-transparent opacity-60" : "bg-[#7625D8]/15 border-[#9C42F5]/25 hover:border-[#9C42F5]/50"}`}>
                <p className="text-sm font-bold leading-snug">{pick(n, "title")}</p>
                <p className="text-[11px] text-[#A5A0D6] mt-1">{fmtRelative(n.created_at)}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
